import * as React from 'react';

import { useMutation } from '@apollo/client';

import { ModalModel, ModalContext } from '@notino/react-styleguide';
import {
  CopyWishlistMutationVariables,
  CopyWishlistMutation as copyWishlistResponse,
} from '@notino/shared/definitions/types';

import { Modal } from '@containers/WishList/components/LinkSharer/Modal';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import { getWindowOrigin } from '@utils/constants';

import copyWishlistMutation from '../../queries/copyWishlist.graphql';

export const useCopyWishlistId = (anonymousId: string) => {
  const { toggleModal } = ModalContext.useModalContext();
  const { getTimeFromInit } = useTrackingContext();

  const [isCopySuccessful, setIsCopySuccessful] = React.useState(true);

  const [copyWishlist, { data }] = useMutation<
    copyWishlistResponse,
    CopyWishlistMutationVariables
  >(copyWishlistMutation);

  const handleOnCopy = React.useCallback(
    async (_: string, success: boolean) => {
      const serverWishlistId = data?.WishlistCopyWishlist?.id;
      if (serverWishlistId !== anonymousId) {
        await copyWishlist({
          variables: { anonymousId },
        });
      }

      dispatchTrackingEvent({
        event: 'wishlist_click',
        wishlist: {
          action: 'wishlist_shared',
          interaction: 'click',
          name: 'share',
          timing: getTimeFromInit(),
        },
      });

      toggleModal(
        <Modal
          hostUrl={getWindowOrigin()}
          anonymousId={anonymousId}
          isCopySuccessful={isCopySuccessful}
        />,
        {
          size: ModalModel.Sizes.default,
          type: ModalModel.Types.default,
          noBorders: true,
        }
      );

      setIsCopySuccessful(success);
    },
    [
      anonymousId,
      data,
      copyWishlist,
      isCopySuccessful,
      toggleModal,
      getTimeFromInit,
    ]
  );

  return {
    handleOnCopy,
  };
};
